import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useView } from "../../context/ViewContext";
import { logoutSession, getValidToken } from "../../utils/auth";
import { useActiveBusiness } from "../../hooks/useActiveBusiness";
import { useConfirmLeave } from "../UnsavedChangesGuard";
import useMediaQuery from "../../hooks/useMediaQuery";
import { resolveBookingPagePath } from "../../utils/bookingPageLink";
import { hasTeamModeAccess } from "../../utils/teamModeAccess";
import {
  calculateOnboardingProgress,
  loadOnboardingSteps,
  ONBOARDING_COMPLETED_EVENT,
} from "../../utils/proOnboarding";
import {
  canUseProfessionalFeatures,
  canUseRecruiterFeatures,
} from "../../utils/roleCapabilities";
import {
  getProNavCounts,
  subscribeProNavCounts,
} from "../../utils/proNavCounts";
import { DEFAULT_DASHBOARD_PATH } from "../../utils/workspaceAuthorization";
import { tabbableIn } from "../../utils/focusable";
import {
  ArrowRightIcon,
  CalendarCheckIcon,
  ChartIcon,
  ChevronDownIcon,
  ClockIcon,
  DollarIcon,
  ExternalLinkIcon,
  EyeIcon,
  MailIcon,
  MapPinIcon,
  MenuIcon,
  MessageIcon,
  PaletteIcon,
  ScissorsIcon,
  ShieldCheckIcon,
  TeamIcon,
  UserIcon,
  UsersIcon,
  XIcon,
} from "../public/icons";
import "./ProfessionalNavbar.css";

// The badges draw their count for the eye only ("99+"), so the link's name
// carries the full count instead: "Bookings (3 need attention)". The leading
// space keeps the words apart where a browser does not add one between spans.
const attentionLabel = (count) => ` (${count} ${count === 1 ? "needs" : "need"} attention)`;

// Initials for the footer avatar (design 3a) — at most two glyphs.
const getInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
};

const ProfessionalNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // The links below are guarded by UnsavedChangesGuard on their own; these
  // buttons navigate from code, so they ask through it.
  const confirmLeave = useConfirmLeave();
  const { setCurrentView } = useView();
  // Read once per mount. getValidToken() returns a new object on every call, so
  // binding it during render re-fired every effect keyed on it.
  const auth = useMemo(() => getValidToken(), []);
  const user = auth?.payload ?? null;
  const userId = user?._id || user?.id || null;
  const userRole = user?.role || null;
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [showOnboardingLink, setShowOnboardingLink] = useState(false);
  const [expandedNavGroups, setExpandedNavGroups] = useState({});
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [navCounts, setNavCounts] = useState(getProNavCounts);
  const accountMenuRef = useRef(null);
  const accountToggleRef = useRef(null);
  const navRef = useRef(null);
  const drawerCloseRef = useRef(null);
  // The button that opened the drawer, read on click: Safari does not focus a
  // button it clicks, so the active element is no record of it.
  const drawerOpenerRef = useRef(null);
  // Matches the drawer breakpoint in pro-tokens.css.
  const isCompact = useMediaQuery("(max-width: 1024px)");
  const {
    activeBusinessId,
    business: activeBusiness,
    membership: activeMembership,
    businesses,
    capabilities: workspaceCapabilities,
    hydrated: workspaceHydrated,
    pendingBusinessId,
    switching,
    switchError,
    switchBusiness,
  } = useActiveBusiness();
  const legacyProfessionalWorkspace = Boolean(canUseProfessionalFeatures(userRole));
  const isSoloProfessional = !activeMembership && legacyProfessionalWorkspace;
  const hasMissingWorkspaceCapabilities = Boolean(activeMembership && !workspaceCapabilities);
  const workspaceSections = workspaceCapabilities?.sections || (
    isSoloProfessional
      ? {
          calendar: true,
          bookings: true,
          clients: true,
          insights: true,
          services: true,
          team: true,
          settings: true,
        }
      : {}
  );
  const isProviderWorkspaceMember = typeof workspaceCapabilities?.canAccessProviderProfile === "boolean"
    ? workspaceCapabilities.canAccessProviderProfile
    : isSoloProfessional;
  const workspaceLandingPath = workspaceCapabilities?.workspaceLandingPath || DEFAULT_DASHBOARD_PATH;
  const showTeamModeNavigation = hasTeamModeAccess(activeBusiness);
  const bookingPagePath = resolveBookingPagePath(activeBusiness, user);
  // Pressing your avatar goes to your own public profile. A workspace member who
  // is not a provider has no profile of their own — resolveBookingPagePath would
  // hand back the business's page — so they keep a plain, unlinked avatar.
  const profilePath = isProviderWorkspaceMember ? bookingPagePath : "";
  const isTeamMember = activeMembership?.role === "team_member";
  const showTeamMemberFinancialActivity = Boolean(
    isTeamMember && activeBusiness?.viewerCapabilities?.canViewFinancialActivity,
  );
  const showTeamMemberStripeSetup = Boolean(
    isTeamMember && activeBusiness?.viewerCapabilities?.providerStripeRequired,
  );
  const workspaceName = activeBusiness?.name || user?.name || "Haastia Pro";
  const accountRoleLabel = activeMembership?.role
    ? activeMembership.role.replace(/_/g, " ")
    : canUseProfessionalFeatures(userRole)
      ? "Owner"
      : userRole || "";

  useEffect(() => {
    setIsMobileNavOpen(false);
    setIsAccountMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => subscribeProNavCounts(setNavCounts), []);

  // The drawer only exists below the breakpoint. Widening the window closes it,
  // or its focus trap and scroll lock would hold on to the desktop rail.
  useEffect(() => {
    if (!isCompact) setIsMobileNavOpen(false);
  }, [isCompact]);

  // The open drawer covers the page, so the keyboard treats it as a dialog:
  // focus moves in on open, Tab stays inside, Escape closes it, and focus goes
  // back to the button that opened it.
  useEffect(() => {
    if (!isMobileNavOpen) return undefined;
    const nav = navRef.current;
    const opener = drawerOpenerRef.current;
    drawerCloseRef.current?.focus();

    const handleKeyDown = (event) => {
      // Something on top already used the key: the leave-without-saving prompt
      // cancelling, or the account actions folding away.
      if (event.defaultPrevented || !nav) return;
      if (event.key === "Escape") {
        setIsMobileNavOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const active = document.activeElement;
      // A dialog opened over the drawer keeps its own focus.
      if (active?.closest?.('[aria-modal="true"]')) return;
      const tabbable = tabbableIn(nav);
      if (!tabbable.length) return;
      const first = tabbable[0];
      const last = tabbable[tabbable.length - 1];
      if (!nav.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      // Hand focus back only if the drawer still had it, not when closing
      // moved it somewhere else on purpose.
      const active = document.activeElement;
      const focusWasInDrawer = !active || active === document.body || nav?.contains(active);
      if (focusWasInDrawer && opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
    };
  }, [isMobileNavOpen]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const bodyClassName = "pro-navbar-mobile-open";
    if (isMobileNavOpen) {
      document.body.classList.add(bodyClassName);
    } else {
      document.body.classList.remove(bodyClassName);
    }
    return () => {
      document.body.classList.remove(bodyClassName);
    };
  }, [isMobileNavOpen]);

  // Close the account disclosure on an outside click, the way a menu should.
  useEffect(() => {
    if (!isAccountMenuOpen || typeof document === "undefined") return undefined;
    const handlePointerDown = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setIsAccountMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isAccountMenuOpen]);

  // Checked once per workspace, not on every route change: the check includes a
  // live Stripe lookup, and the completion event below hides the link as soon
  // as the checklist or any later check finds onboarding finished. It waits for
  // the workspace to hydrate, since the token's partial business can name the
  // wrong scope and the check would only run again once it lands.
  const hasActiveBusiness = Boolean(activeBusiness);
  const activeBusinessRole = activeMembership?.role || null;
  useEffect(() => {
    let isMounted = true;

    const syncOnboardingVisibility = async () => {
      if (!userId || (hasActiveBusiness && !isProviderWorkspaceMember)) {
        if (isMounted) {
          setShowOnboardingLink(false);
        }
        return;
      }
      if (!workspaceHydrated) return;

      try {
        const steps = await loadOnboardingSteps(userId, {
          activeBusinessId: showTeamModeNavigation ? activeBusinessId : null,
          teamMode: showTeamModeNavigation,
          businessRole: activeBusinessRole,
        });
        const { isComplete } = calculateOnboardingProgress(steps);

        if (isMounted) {
          setShowOnboardingLink(!isComplete);
        }
      } catch (error) {
        if (isMounted) {
          setShowOnboardingLink(false);
        }
      }
    };

    syncOnboardingVisibility();

    return () => {
      isMounted = false;
    };
  }, [
    activeBusinessId,
    activeBusinessRole,
    hasActiveBusiness,
    isProviderWorkspaceMember,
    showTeamModeNavigation,
    userId,
    workspaceHydrated,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleOnboardingCompleted = (event) => {
      const completedUserId = event?.detail?.userId || null;
      if (completedUserId && userId && completedUserId !== userId) {
        return;
      }

      setShowOnboardingLink(false);
    };

    window.addEventListener(ONBOARDING_COMPLETED_EVENT, handleOnboardingCompleted);
    return () => {
      window.removeEventListener(ONBOARDING_COMPLETED_EVENT, handleOnboardingCompleted);
    };
  }, [userId]);

  const handleLogout = () => confirmLeave(async () => {
    await logoutSession();
    setCurrentView("customer");
    navigate("/login");
  });

  const switchToCustomer = () => confirmLeave(() => {
    setCurrentView("customer");
    navigate("/");
  });

  const toggleMobileNav = (event) => {
    drawerOpenerRef.current = event?.currentTarget || null;
    setIsMobileNavOpen((prev) => !prev);
  };

  const toggleAccountMenu = () => {
    setIsAccountMenuOpen((prev) => !prev);
  };

  // A disclosure, not a menu: Tab moves through its two buttons like any
  // others, and Escape folds it back onto its toggle without also closing the
  // drawer around it.
  const handleAccountKeyDown = (event) => {
    if (event.key !== "Escape" || !isAccountMenuOpen) return;
    event.preventDefault();
    setIsAccountMenuOpen(false);
    accountToggleRef.current?.focus();
  };

  const closeMobileNav = () => {
    setIsMobileNavOpen(false);
  };

  const handleBusinessChange = async (event) => {
    const nextBusinessId = event.target.value;
    const switched = await switchBusiness(nextBusinessId);
    if (switched) {
      const nextBusiness = businesses.find((business) => business._id === nextBusinessId);
      navigate(nextBusiness?.viewerCapabilities?.workspaceLandingPath || DEFAULT_DASHBOARD_PATH);
    }
  };

  const toggleNavGroup = (groupName) => () => {
    setExpandedNavGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const linkClassName = ({ isActive }) =>
    `pro-nav__item${isActive ? " pro-nav__item--active" : ""}`;
  const subLinkClassName = ({ isActive }) =>
    `pro-nav__subitem${isActive ? " pro-nav__subitem--active" : ""}`;

  const isAvailabilityNavOpen = Boolean(expandedNavGroups.availability)
    || location.pathname.startsWith("/availability");
  const isBookingPageNavOpen = Boolean(expandedNavGroups.bookingPage)
    || location.pathname.startsWith("/booking-page-builder")
    || location.pathname.startsWith("/profile-appearance");

  // The mobile top bar names the screen, so the page body never repeats it.
  const mobileTitle = useMemo(() => {
    const titles = [
      ["/pro/overview", "Overview"],
      ["/calendar", "Calendar"],
      ["/bookings", "Bookings"],
      ["/availability", "Availability"],
      ["/customers", "Client list"],
      ["/client-insights", "Insights"],
      ["/pro/import", "Import data"],
      ["/services", "Services"],
      ["/add-service", "Services"],
      ["/booking-page-builder", "Booking page"],
      ["/profile-appearance", "Booking page"],
      ["/booking-questions", "Pre-booking questions"],
      ["/team", "Team"],
      ["/payments/activity", "Financial activity"],
      ["/payments", "Payouts"],
      ["/pro/settings", "Settings"],
      ["/feedback", "Feedback & support"],
      ["/onboarding", "Getting started"],
      ["/recruiter", "Referrals"],
    ];
    const match = titles.find(([path]) => location.pathname.startsWith(path));
    return match ? match[1] : "Haastia Pro";
  }, [location.pathname]);

  const bookingsCount = navCounts.bookings;
  const teamCount = navCounts.team;

  const renderCount = (value, variant) => {
    if (!value) return null;
    return (
      <>
        <span className={`pro-count${variant ? ` pro-count--${variant}` : ""}`} aria-hidden="true">
          {value > 99 ? "99+" : value}
        </span>
        <span className="sr-only">{attentionLabel(value)}</span>
      </>
    );
  };

  const accountIdentity = (
    <>
      <span className="pro-avatar" aria-hidden="true">{getInitials(user?.name)}</span>
      <span className="pro-nav__accounttext">
        <span className="pro-nav__accountname">{user?.name || "Signed in"}</span>
        {accountRoleLabel ? (
          <span className="pro-nav__accountrole">{accountRoleLabel}</span>
        ) : null}
      </span>
    </>
  );

  const navigation = (
    <div className="pro-nav__groups">
      {showOnboardingLink && isProviderWorkspaceMember && (
        <div className="pro-nav__group">
          <NavLink
            to={showTeamModeNavigation ? "/onboarding/team" : "/onboarding"}
            className={({ isActive }) =>
              `pro-nav__item pro-nav__item--highlight${isActive ? " pro-nav__item--active" : ""}`
            }
            onClick={closeMobileNav}
          >
            <ShieldCheckIcon className="pro-nav__icon" />
            <span>Getting started</span>
          </NavLink>
        </div>
      )}

      <div className="pro-nav__group">
        <p className="pro-nav__grouplabel">Schedule</p>
        <NavLink to="/pro/overview" className={linkClassName} onClick={closeMobileNav}>
          <EyeIcon className="pro-nav__icon" />
          <span>Overview</span>
        </NavLink>
        {workspaceSections.calendar && (
          <NavLink to="/calendar" className={linkClassName} onClick={closeMobileNav}>
            <CalendarCheckIcon className="pro-nav__icon" />
            <span>Calendar</span>
          </NavLink>
        )}
        {/* Not `end`: a booking's own page, /bookings/:id, is still Bookings. */}
        {workspaceSections.bookings && (
          <NavLink to="/bookings" className={linkClassName} onClick={closeMobileNav}>
            <ClockIcon className="pro-nav__icon" />
            <span className="pro-nav__label">Bookings</span>
            {renderCount(bookingsCount, "bad")}
          </NavLink>
        )}
        {isProviderWorkspaceMember && (
          <div className="pro-nav__sub">
            <button
              type="button"
              className="pro-nav__item pro-nav__trigger"
              onClick={toggleNavGroup("availability")}
              aria-expanded={isAvailabilityNavOpen}
              aria-controls="professional-availability-subnav"
            >
              <MapPinIcon className="pro-nav__icon" />
              <span className="pro-nav__label">Availability</span>
              <ChevronDownIcon
                className={`pro-nav__chevron${isAvailabilityNavOpen ? " pro-nav__chevron--open" : ""}`}
                size={14}
              />
            </button>
            {isAvailabilityNavOpen && (
              <div className="pro-nav__sublist" id="professional-availability-subnav">
                <NavLink to="/availability" className={subLinkClassName} onClick={closeMobileNav} end>
                  Weekly hours
                </NavLink>
                <NavLink to="/availability/limits" className={subLinkClassName} onClick={closeMobileNav}>
                  Scheduling limits
                </NavLink>
              </div>
            )}
          </div>
        )}
      </div>

      {(workspaceSections.clients || workspaceSections.insights || workspaceCapabilities?.canImportBusinessData) && (
        <div className="pro-nav__group">
          <p className="pro-nav__grouplabel">Clients</p>
          {workspaceSections.clients && (
            <NavLink to="/customers" className={linkClassName} onClick={closeMobileNav}>
              <UsersIcon className="pro-nav__icon" />
              <span>Client list</span>
            </NavLink>
          )}
          {workspaceSections.insights && (
            <NavLink to="/client-insights" className={linkClassName} onClick={closeMobileNav}>
              <ChartIcon className="pro-nav__icon" />
              <span>Insights</span>
            </NavLink>
          )}
          {/* Bringing a client list across is a manager's job, so this follows
              the capability rather than the clients section. */}
          {workspaceCapabilities?.canImportBusinessData && (
            <NavLink to="/pro/import" className={linkClassName} onClick={closeMobileNav}>
              <ArrowRightIcon className="pro-nav__icon" />
              <span>Import data</span>
            </NavLink>
          )}
        </div>
      )}

      <div className="pro-nav__group">
        <p className="pro-nav__grouplabel">Business</p>
        {workspaceSections.services && (
          <NavLink to="/services" className={linkClassName} onClick={closeMobileNav}>
            <ScissorsIcon className="pro-nav__icon" />
            <span>Services</span>
          </NavLink>
        )}
        {isProviderWorkspaceMember && (
          <div className="pro-nav__sub">
            <button
              type="button"
              className="pro-nav__item pro-nav__trigger"
              onClick={toggleNavGroup("bookingPage")}
              aria-expanded={isBookingPageNavOpen}
              aria-controls="professional-booking-page-subnav"
            >
              <PaletteIcon className="pro-nav__icon" />
              <span className="pro-nav__label">Booking page</span>
              <ChevronDownIcon
                className={`pro-nav__chevron${isBookingPageNavOpen ? " pro-nav__chevron--open" : ""}`}
                size={14}
              />
            </button>
            {isBookingPageNavOpen && (
              <div className="pro-nav__sublist" id="professional-booking-page-subnav">
                <NavLink to="/booking-page-builder" className={subLinkClassName} onClick={closeMobileNav} end>
                  Page content
                </NavLink>
                <NavLink to="/profile-appearance" className={subLinkClassName} onClick={closeMobileNav}>
                  Appearance
                </NavLink>
                <NavLink to="/booking-page-visual-editor" className={subLinkClassName} onClick={closeMobileNav} end>
                  Visual editor
                </NavLink>
              </div>
            )}
          </div>
        )}
        {isProviderWorkspaceMember && (
          <NavLink to="/booking-questions" className={linkClassName} onClick={closeMobileNav}>
            <MessageIcon className="pro-nav__icon" />
            <span>Pre-booking questions</span>
          </NavLink>
        )}
        {showTeamModeNavigation && workspaceSections.team && (
          <NavLink to="/team" className={linkClassName} onClick={closeMobileNav}>
            <TeamIcon className="pro-nav__icon" />
            <span className="pro-nav__label">Team</span>
            {renderCount(teamCount)}
          </NavLink>
        )}
        {isProviderWorkspaceMember && canUseProfessionalFeatures(userRole) && !isTeamMember && (
          <NavLink to="/payments/connect" className={linkClassName} onClick={closeMobileNav}>
            <DollarIcon className="pro-nav__icon" />
            <span>Payouts</span>
          </NavLink>
        )}
        {showTeamMemberFinancialActivity && (
          <NavLink to="/payments/activity" className={linkClassName} onClick={closeMobileNav}>
            <DollarIcon className="pro-nav__icon" />
            <span>Financial activity</span>
          </NavLink>
        )}
        {showTeamMemberStripeSetup && (
          <NavLink to="/payments/connect" className={linkClassName} onClick={closeMobileNav}>
            <ShieldCheckIcon className="pro-nav__icon" />
            <span>Stripe setup</span>
          </NavLink>
        )}
      </div>

      <div className="pro-nav__group">
        <p className="pro-nav__grouplabel">Account</p>
        {/* Not `end` either: /pro/settings/account and the after-booking
            messages page are Settings too. */}
        {workspaceSections.settings && (
          <NavLink to="/pro/settings" className={linkClassName} onClick={closeMobileNav}>
            <UserIcon className="pro-nav__icon" />
            <span>Settings</span>
          </NavLink>
        )}
        {canUseRecruiterFeatures(userRole) && (
          <NavLink to="/recruiter" className={linkClassName} onClick={closeMobileNav}>
            <ArrowRightIcon className="pro-nav__icon" />
            <span>Referrals</span>
          </NavLink>
        )}
        <NavLink to="/feedback" className={linkClassName} onClick={closeMobileNav}>
          <MailIcon className="pro-nav__icon" />
          <span>Feedback &amp; support</span>
        </NavLink>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile top bar (design 3j): menu, the screen's name, the account avatar. */}
      <header className="pro-topbar">
        <div className="pro-topbar__lead">
          <button
            type="button"
            className="pro-topbar__menu"
            onClick={toggleMobileNav}
            aria-expanded={isMobileNavOpen}
            aria-controls="professional-navigation"
            aria-hidden={isMobileNavOpen}
            hidden={isMobileNavOpen}
            tabIndex={isMobileNavOpen ? -1 : 0}
          >
            <MenuIcon size={22} />
            <span className="sr-only">Toggle navigation</span>
          </button>
          <p className="pro-topbar__title">{mobileTitle}</p>
        </div>
        {profilePath ? (
          <Link to={profilePath} className="pro-topbar__profile" onClick={closeMobileNav}>
            <span className="pro-avatar pro-avatar--sm" aria-hidden="true">
              {getInitials(user?.name)}
            </span>
            <span className="sr-only">Your profile</span>
          </Link>
        ) : (
          <span className="pro-avatar pro-avatar--sm" aria-hidden="true">
            {getInitials(user?.name)}
          </span>
        )}
      </header>

      {isMobileNavOpen && (
        <div className="pro-nav__backdrop" onClick={closeMobileNav} />
      )}

      <nav
        ref={navRef}
        id="professional-navigation"
        className={`pro-nav${isMobileNavOpen ? " pro-nav--open" : ""}`}
        aria-label="Dashboard"
      >
        <div className="pro-nav__brand">
          <button
            type="button"
            className="pro-nav__brandbutton"
            onClick={() => confirmLeave(() => {
              closeMobileNav();
              navigate(workspaceLandingPath);
            }, { to: workspaceLandingPath })}
          >
            <span className="pro-nav__mark" aria-hidden="true">h</span>
            <span className="pro-nav__brandtext">
              <span className="pro-nav__brandname">{workspaceName}</span>
              <span className="pro-nav__brandsub">Pro dashboard</span>
            </span>
          </button>
          <button
            ref={drawerCloseRef}
            type="button"
            className="pro-nav__close"
            onClick={closeMobileNav}
            aria-label="Close navigation"
          >
            <XIcon size={18} />
          </button>
        </div>

        {businesses.length > 1 && (
          <div className="pro-nav__workspace">
            <label className="pro-nav__workspacelabel" htmlFor="active-business-select">
              Workspace
            </label>
            <select
              id="active-business-select"
              className="pro-select pro-nav__workspaceselect"
              value={pendingBusinessId || activeBusinessId || ""}
              onChange={handleBusinessChange}
              aria-label="Active business workspace"
              aria-busy={switching}
              disabled={switching}
            >
              {businesses.map((business) => (
                <option key={business._id} value={business._id}>
                  {business.name}
                </option>
              ))}
            </select>
            <div className="pro-nav__workspacestatus" aria-live="polite">
              {switching ? "Switching workspace…" : switchError}
            </div>
          </div>
        )}

        {hasMissingWorkspaceCapabilities ? (
          <div className="pro-nav__workspacestatus" role="alert">
            Workspace permissions are unavailable. Navigation is hidden until they can be refreshed.
          </div>
        ) : null}

        {navigation}

        <div className="pro-nav__footer">
          {isProviderWorkspaceMember && bookingPagePath && (
            <NavLink
              to={bookingPagePath}
              className="pro-nav__booking"
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLinkIcon className="pro-nav__icon" />
              <span className="pro-nav__label">View your booking page</span>
            </NavLink>
          )}

          <div className="pro-nav__account" ref={accountMenuRef} onKeyDown={handleAccountKeyDown}>
            {profilePath ? (
              // Split row: the name and avatar are the way to your profile, so the
              // disclosure keeps only the chevron. A link nested inside the
              // toggle button would not be valid markup.
              <div className="pro-nav__accountrow">
                <Link to={profilePath} className="pro-nav__accountlink" onClick={closeMobileNav}>
                  {accountIdentity}
                </Link>
                <button
                  ref={accountToggleRef}
                  type="button"
                  className="pro-nav__accounttoggle"
                  onClick={toggleAccountMenu}
                  aria-expanded={isAccountMenuOpen}
                  aria-controls="professional-account-actions"
                  aria-label="Account menu"
                >
                  <ChevronDownIcon
                    className={`pro-nav__chevron${isAccountMenuOpen ? " pro-nav__chevron--open" : ""}`}
                    size={14}
                  />
                </button>
              </div>
            ) : (
              <button
                ref={accountToggleRef}
                type="button"
                className="pro-nav__accountbutton"
                onClick={toggleAccountMenu}
                aria-expanded={isAccountMenuOpen}
                aria-controls="professional-account-actions"
              >
                {accountIdentity}
                <ChevronDownIcon
                  className={`pro-nav__chevron${isAccountMenuOpen ? " pro-nav__chevron--open" : ""}`}
                  size={14}
                />
              </button>
            )}

            {isAccountMenuOpen && (
              <div className="pro-nav__accountmenu" id="professional-account-actions">
                <button type="button" onClick={switchToCustomer}>
                  Exit Dashboard
                </button>
                <button type="button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile tab bar (design 3j): 56px rows, 24px glyphs, safe-area padding.
          Only mounted below the breakpoint — a display:none copy of the rail's
          links would still duplicate every one of them for assistive tech. */}
      {isCompact ? (
      <nav className="pro-tabbar" aria-label="Dashboard shortcuts">
        <NavLink
          to="/pro/overview"
          className={({ isActive }) => `pro-tabbar__item${isActive ? " pro-tabbar__item--active" : ""}`}
        >
          <EyeIcon size={24} />
          <span>Overview</span>
        </NavLink>
        {workspaceSections.calendar && (
          <NavLink
            to="/calendar"
            className={({ isActive }) => `pro-tabbar__item${isActive ? " pro-tabbar__item--active" : ""}`}
          >
            <CalendarCheckIcon size={24} />
            <span>Calendar</span>
          </NavLink>
        )}
        {workspaceSections.bookings && (
          <NavLink
            to="/bookings"
            className={({ isActive }) => `pro-tabbar__item${isActive ? " pro-tabbar__item--active" : ""}`}
          >
            <span className="pro-tabbar__glyph">
              <ClockIcon size={24} />
              {bookingsCount ? (
                <span className="pro-tabbar__badge" aria-hidden="true">
                  {bookingsCount > 9 ? "9+" : bookingsCount}
                </span>
              ) : null}
            </span>
            <span>Bookings</span>
            {bookingsCount ? <span className="sr-only">{attentionLabel(bookingsCount)}</span> : null}
          </NavLink>
        )}
        <button
          type="button"
          className={`pro-tabbar__item${isMobileNavOpen ? " pro-tabbar__item--active" : ""}`}
          onClick={toggleMobileNav}
          aria-expanded={isMobileNavOpen}
          aria-controls="professional-navigation"
        >
          <MenuIcon size={24} />
          <span>More</span>
        </button>
      </nav>
      ) : null}
    </>
  );
};

export default ProfessionalNavbar;
